import React, { useState, useEffect, useCallback } from 'react';
import { GripVertical, Eye, EyeOff, Save, RefreshCw, Settings, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';
import getApiUrl from '../../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * DashboardManager - Gestionnaire de l'ordre et visibilité des dashboards
 * Permet de drag & drop pour réorganiser et toggle pour afficher/masquer
 */
export default function DashboardManager() {
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedItem, setDraggedItem] = useState(null);

  // Liste par défaut des dashboards avec leur config
  const defaultDashboards = [
    { id: 'recherche', label: '🔍 Recherche', description: 'Recherche multi-critères', category: 'general', visible: true },
    { id: 'salle_reunion', label: '🏛️ Salle de Réunion', description: 'Réunions virtuelles IA', category: 'general', visible: true },
    { id: 'telepro', label: '📞 Télépro', description: 'Prospection téléphonique', category: 'commercial', visible: true },
    { id: 'r1', label: '🔍 R1', description: 'Prévisite audit technique', category: 'commercial', visible: true },
    { id: 'administratif', label: '📋 Administratif', description: 'Gestion des dossiers', category: 'general', visible: true },
    { id: 'amo', label: '🏢 AMO', description: 'Assistance maîtrise d\'ouvrage', category: 'technique', visible: true },
    { id: 'r2', label: '📐 R2', description: 'Sortie architecte', category: 'technique', visible: true },
    { id: 'chantiers', label: '🚚 Chantiers', description: 'Suivi des travaux', category: 'technique', visible: true },
    { id: 'factures_fournisseurs', label: '📄 Factures Fournisseurs', description: 'Dépôt & suivi factures', category: 'compta', visible: true },
    { id: 'aides_comp', label: '💝 Aides Comp.', description: 'Aides complémentaires', category: 'general', visible: true },
    { id: 'tc_pac_iso', label: '⚙️ TC/PAC ISO', description: 'Techniques & PAC', category: 'technique', visible: true },
    { id: 'communications_ia', label: '📨 Communications IA', description: 'Traitement & Enrichissement', category: 'general', visible: true },
    { id: 'marketing', label: '📣 Marketing & Com', description: 'Acquisition & Communication', category: 'general', visible: true },
    { id: 'grands_comptes', label: '🏢 Grands Comptes', description: 'CCAS & Prescripteurs', category: 'commercial', visible: true },
    { id: 'calendrier', label: '📅 Calendrier', description: 'RDV R1 & R2', category: 'general', visible: true },
    { id: 'comite_direction', label: '👔 Comité de Direction', description: 'Échanges avec les directeurs IA', category: 'direction', visible: true },
    { id: 'formations', label: '📚 Formations', description: 'Formation MaPrimeAdapt\' & Simulations', category: 'general', visible: true },
    { id: 'simulateur_aides', label: '🏠 Simulateur Aides', description: 'Calcul aides rénovation', category: 'general', visible: true },
    { id: 'super_batard', label: '😈 Super Batard', description: 'Admin & Configuration IA', category: 'admin', visible: true },
    // Dashboards Compta (à la fin par défaut)
    { id: 'compta_finances', label: '💰 Compta / Finances', description: 'Rapprochements & Stats', category: 'compta', visible: true },
    { id: 'compta_fi_new', label: '🚀 COMPTA FI NEW', description: 'Validation Pennylane', category: 'compta', visible: true },
    { id: 'consolidation_v2', label: '🧪 Compta (Test)', description: 'Version test consolidation', category: 'compta', visible: true },
    { id: 'super_batard_compta', label: '🔴 Admin Compta', description: 'Administration comptabilité', category: 'compta', visible: true },
  ];

  // Charger la config
  const loadConfig = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/config/dashboards-order`);
      if (res.ok) {
        const data = await res.json();
        if (data.dashboards && data.dashboards.length > 0) {
          // Merger avec les défauts pour avoir les nouveaux dashboards
          const savedIds = data.dashboards.map(d => d.id);
          const newDashboards = defaultDashboards.filter(d => !savedIds.includes(d.id));
          setDashboards([...data.dashboards, ...newDashboards]);
        } else {
          setDashboards(defaultDashboards);
        }
      } else {
        setDashboards(defaultDashboards);
      }
    } catch (e) {
      console.error('Erreur chargement config dashboards:', e);
      setDashboards(defaultDashboards);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Sauvegarder la config
  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/api/config/dashboards-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dashboards })
      });
      
      if (res.ok) {
        toast.success('Configuration sauvegardée !');
      } else {
        toast.error('Erreur de sauvegarde');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  // Toggle visibilité
  const toggleVisibility = (id) => {
    setDashboards(prev => prev.map(d => 
      d.id === id ? { ...d, visible: !d.visible } : d
    ));
  };

  // Drag & Drop handlers
  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;
    
    const newDashboards = [...dashboards];
    const draggedDashboard = newDashboards[draggedItem];
    newDashboards.splice(draggedItem, 1);
    newDashboards.splice(index, 0, draggedDashboard);
    
    setDashboards(newDashboards);
    setDraggedItem(index);
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
  };

  // Catégories pour le groupement visuel
  const getCategoryColor = (category) => {
    const colors = {
      general: 'bg-slate-500/20 text-slate-400',
      commercial: 'bg-blue-500/20 text-blue-400',
      technique: 'bg-orange-500/20 text-orange-400',
      compta: 'bg-emerald-500/20 text-emerald-400',
      admin: 'bg-red-500/20 text-red-400',
      direction: 'bg-amber-500/20 text-amber-400'
    };
    return colors[category] || colors.general;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      general: 'Général',
      commercial: 'Commercial',
      technique: 'Technique',
      compta: 'Comptabilité',
      admin: 'Administration',
      direction: 'Direction'
    };
    return labels[category] || category;
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-slate-400">
        <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
        Chargement de la configuration...
      </div>
    );
  }

  const visibleCount = dashboards.filter(d => d.visible).length;
  const hiddenCount = dashboards.filter(d => !d.visible).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <LayoutDashboard className="w-6 h-6 text-blue-400" />
            Gestion des Dashboards
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Glissez-déposez pour réorganiser • Cliquez sur l'œil pour afficher/masquer
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="text-sm text-slate-400">
            <span className="text-green-400">{visibleCount} visibles</span>
            {hiddenCount > 0 && <span className="ml-2 text-red-400">{hiddenCount} masqués</span>}
          </div>
          
          <button
            onClick={saveConfig}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-medium transition-all disabled:opacity-50"
            data-testid="save-dashboards-order"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Sauvegarder
          </button>
        </div>
      </div>

      {/* Liste des dashboards */}
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
        <div className="space-y-2">
          {dashboards.map((dashboard, index) => (
            <div
              key={dashboard.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move ${
                draggedItem === index 
                  ? 'border-blue-500 bg-blue-500/10 scale-[1.02]' 
                  : dashboard.visible 
                    ? 'border-slate-600 bg-slate-700/50 hover:border-slate-500' 
                    : 'border-slate-700 bg-slate-800/50 opacity-60'
              }`}
              data-testid={`dashboard-row-${dashboard.id}`}
            >
              {/* Grip pour drag */}
              <div className="text-slate-500 hover:text-slate-300">
                <GripVertical className="w-5 h-5" />
              </div>
              
              {/* Position */}
              <div className="w-8 h-8 flex items-center justify-center bg-slate-800 rounded-lg text-slate-400 text-sm font-mono">
                {index + 1}
              </div>
              
              {/* Infos */}
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className={`font-medium ${dashboard.visible ? 'text-white' : 'text-slate-400'}`}>
                    {dashboard.label}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded ${getCategoryColor(dashboard.category)}`}>
                    {getCategoryLabel(dashboard.category)}
                  </span>
                </div>
                <p className="text-xs text-slate-500">{dashboard.description}</p>
              </div>
              
              {/* ID technique */}
              <div className="text-xs text-slate-600 font-mono">
                {dashboard.id}
              </div>
              
              {/* Toggle visibilité */}
              <button
                onClick={() => toggleVisibility(dashboard.id)}
                className={`p-2 rounded-lg transition-all ${
                  dashboard.visible 
                    ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                    : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                }`}
                title={dashboard.visible ? 'Masquer sur la home' : 'Afficher sur la home'}
                data-testid={`toggle-${dashboard.id}`}
              >
                {dashboard.visible ? (
                  <Eye className="w-5 h-5" />
                ) : (
                  <EyeOff className="w-5 h-5" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <h4 className="font-medium text-blue-400 flex items-center gap-2 mb-2">
          <Settings className="w-4 h-4" />
          Comment ça marche ?
        </h4>
        <ul className="text-sm text-slate-300 space-y-1">
          <li>• <strong>Glissez-déposez</strong> les dashboards pour changer leur ordre sur la page d'accueil</li>
          <li>• Cliquez sur <Eye className="w-4 h-4 inline text-green-400" /> pour <strong>masquer</strong> un dashboard de la home</li>
          <li>• Cliquez sur <EyeOff className="w-4 h-4 inline text-red-400" /> pour <strong>afficher</strong> un dashboard masqué</li>
          <li>• N'oubliez pas de <strong>sauvegarder</strong> vos modifications !</li>
          <li>• Les permissions utilisateurs (Gestion des accès) restent prioritaires</li>
        </ul>
      </div>
    </div>
  );
}
